/**
 * What is under the cursor, and what can be done to it.
 *
 * The menu is built entirely on this side. The server sends every name, examine
 * line and menu option once at login, so opening a menu costs nothing and works at
 * any latency. Choosing an entry sends intent, and the server decides what actually
 * happens, exactly as it does for a click on the ground.
 */

import type {
  DefinitionsPacket,
  ItemDefinitionWire,
  NpcDefinitionWire,
  ObjectDefinitionWire,
} from '@inverse/protocol';

export type MenuTarget =
  | { kind: 'ground'; tx: number; tz: number; plane: number }
  | { kind: 'npc'; entityId: number; defId: number; name: string; combatLevel: number }
  | { kind: 'player'; entityId: number; name: string; combatLevel: number }
  | {
      kind: 'object';
      defId: number;
      tx: number;
      tz: number;
      plane: number;
      name: string;
      /** Which of the drawn copies it is, so the one under the pointer can be lit. */
      index?: number;
    }
  | { kind: 'pile'; pileId: number; itemId: number; quantity: number; name: string };

export interface MenuEntry {
  label: string;
  /** Extra text on the right of the row, such as a target's combat level. */
  detail?: string;
  /**
   * True for an option whose system is not built yet. Shown, because leaving it out
   * would suggest it is not coming, and greyed, because pretending it works is worse.
   */
  pending?: boolean;
  /** The choice already made, among several: a gauge's shape, say. */
  checked?: boolean;
  /** A label over the entries after it rather than something to press. */
  heading?: boolean;
  run(): void;
}

export interface ContextMenu {
  screenX: number;
  screenY: number;
  title: string;
  entries: MenuEntry[];
}

/** Everything the client was told about NPCs, scenery and items at login. */
export class Definitions {
  private readonly npcs = new Map<number, NpcDefinitionWire>();
  private readonly objects = new Map<number, ObjectDefinitionWire>();
  private readonly items = new Map<number, ItemDefinitionWire>();

  apply(packet: DefinitionsPacket): void {
    this.npcs.clear();
    this.objects.clear();
    this.items.clear();
    for (const npc of packet.npcs) this.npcs.set(npc.id, npc);
    for (const object of packet.objects) this.objects.set(object.id, object);
    for (const item of packet.items) this.items.set(item.id, item);
  }

  item(id: number): ItemDefinitionWire | undefined {
    return this.items.get(id);
  }

  npc(id: number): NpcDefinitionWire | undefined {
    return this.npcs.get(id);
  }

  object(id: number): ObjectDefinitionWire | undefined {
    return this.objects.get(id);
  }
}

/**
 * Whether the only thing on offer is a look at it.
 *
 * Which is to say: nothing. A pine planted as scenery, a boulder, a fence post.
 * The pointer goes through these, so a wood can be walked into and somebody
 * standing behind a tree can be clicked on, and examining one is on the
 * right-click menu where the rest of what is there is listed.
 */
export function onlyLookedAt(definition: { actions: readonly string[]; piece?: boolean }): boolean {
  return definition.actions.every((action) => {
    const named = action.trim().toLowerCase();
    return named === '' || named === 'examine';
  });
}

/**
 * What a click on something would amount to, for the colour it glows.
 *
 * Not the action itself: half a dozen words all mean "open this" and there is no
 * use in a colour for each. Fighting is one thing, taking is another, trading is
 * a third, and everything else is dealing with something.
 */
export type Meaning = 'walk' | 'attack' | 'talk' | 'trade' | 'gather' | 'take' | 'use';

export function meaningOf(
  target: MenuTarget,
  action: string | null,
  /**
   * Whether this is somewhere a skill is worked, which the world knows and the
   * words on a menu do not. A fishing spot says Net, Lure, Cage and Harpoon,
   * none of which look like gathering to a list of verbs, and it was lit the
   * colour of a door for it.
   */
  gathered = false,
): Meaning {
  if (target.kind === 'ground') return 'walk';
  if (target.kind === 'pile') return 'take';
  if (target.kind === 'player') return 'talk';

  const named = (action ?? '').toLowerCase();

  // Something that can only be looked at is not on offer. A wood of scenery
  // pines lighting up one at a time as the pointer crosses them says every one
  // of them can be worked, and none of them can: a highlight is an offer, and
  // examining is not one. Characters keep theirs, since walking up to somebody
  // and looking at them is a thing people do.
  if (target.kind === 'object' && (named === '' || named === 'examine')) return 'walk';

  // Whole words. Examine contains "mine", so looking at anybody was lighting
  // them up the colour of a rock face and offering to dig them.
  if (/\b(attack|fight|kill)\b/.test(named)) return 'attack';
  if (/\b(trade|shop|buy|sell|bank)\b/.test(named)) return 'trade';
  if (/\b(talk|speak|ask)\b/.test(named)) return 'talk';
  if (gathered) return 'gather';
  if (/\b(chop|mine|fish|cut|pick|catch|dig)\b/.test(named)) return 'gather';
  return 'use';
}

/** What each of those looks like. Red for a fight, and nothing else is red. */
export const MEANING_COLOURS: Readonly<Record<Meaning, number>> = {
  walk: 0xffffff,
  attack: 0xff5a4a,
  talk: 0x5aa6ff,
  trade: 0x63d68a,
  gather: 0xffc861,
  take: 0xd8b6ff,
  use: 0x5aa6ff,
};
